import { useState } from 'react'
import { Bell } from 'lucide-react'
import { cn } from '../../lib/utils'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import { NotificationsPopover } from './NotificationsPopover'
import { useNotificationCounts } from './useNotificationCounts'

export interface NotificationsBellProps {
  className?: string
}

/**
 * Persistent bell mounted in the AppLayout topbar. Click opens the popover.
 *
 * Decoration rules (see useNotificationCounts):
 *   open Needs-you rows → red number badge
 *   none, but unread activity (when shown) → small dot
 *   otherwise → bell with no decoration
 */
export function NotificationsBell({ className }: NotificationsBellProps) {
  const { importantCount, hasUnreadInfo } = useNotificationCounts()
  const [open, setOpen] = useState(false)
  const display = importantCount > 9 ? '9+' : String(importantCount)
  const showNumber = importantCount > 0
  const showDot = !showNumber && hasUnreadInfo

  let ariaLabel = 'Notifications'
  if (showNumber) {
    ariaLabel = `Notifications (${importantCount} active)`
  } else if (showDot) {
    ariaLabel = 'Notifications (unread)'
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          className={cn(
            'relative flex h-9 w-9 cursor-pointer items-center justify-center rounded-md',
            'border border-transparent bg-transparent shadow-none',
            'hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            className
          )}
        >
          <Bell className="h-4 w-4" />
          {showNumber ? (
            <span
              data-testid="notifications-bell-badge"
              className="absolute -right-1 -top-1 flex min-w-4 items-center justify-center rounded-full bg-st-down px-1 text-10 font-semibold leading-4 text-white"
            >
              {display}
            </span>
          ) : showDot ? (
            <span
              data-testid="notifications-bell-dot"
              aria-hidden="true"
              className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-ink-3"
            />
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="flex max-h-(--radix-popover-content-available-height) w-100 max-w-[calc(100vw-1.5rem)] flex-col overflow-hidden p-0"
      >
        <NotificationsPopover onClose={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  )
}
